import { Trans } from 'react-i18next';

import type { ReactNode } from 'react';

import type { SubscriptionBillingCycle, SubscriptionPlan } from '@bugbug/core/types/subscriptions';
import { SUBSCRIPTION_PERIOD, SUBSCRIPTION_TYPE } from '~/constants/subscription';
import i18n from '~/translations';

import { MainFeatureIcon } from '../PlanFeature/PlanFeature.styled';

export const MAX_PARALLEL_RUNS = 31;
export const SUBSCRIPTION_PERIOD_DETAILS: Record<
  SubscriptionBillingCycle,
  {
    name: string;
    discount: string;
  }
> = {
  [SUBSCRIPTION_PERIOD.YEARLY]: {
    name: i18n.t('manageSubscription.period.yearly.label', 'Annually'),
    discount: i18n.t('manageSubscription.period.yearly.discount', '0.15'),
  },
  [SUBSCRIPTION_PERIOD.MONTHLY]: {
    name: i18n.t('manageSubscription.period.monthly.label', 'Monthly'),
    discount: i18n.t('manageSubscription.period.monthly.discount', '0'),
  },
};

export const PENDING_DOWNGRADE_INFO = i18n.t(
  'manageSubscription.pendingDowngrade',
  'Your downgrade is pending. Please first cancel the downgrade or contact support.',
);

export interface PlanFeature {
  label: ReactNode;
  icon?: ReactNode;
  tooltip?: string;
}

export const SUBSCRIPTION_DETAILS: Record<
  SubscriptionPlan['type'],
  {
    name: string;
    description: string;
    mainFeatures: PlanFeature[];
    otherFeaturesTitle: string;
    features: PlanFeature[];
  }
> = {
  [SUBSCRIPTION_TYPE.FREE]: {
    name: i18n.t('manageSubscription.plan.free.name', 'Free'),
    description: i18n.t(
      'manageSubscription.plan.free.description',
      'For individuals who want to start testing simple web apps',
    ),
    mainFeatures: [
      {
        label: (
          <Trans i18nKey="manageSubscription.features.users.label">
            <b>Unlimited</b> users
          </Trans>
        ),
        icon: <MainFeatureIcon name="users" />,
      },
      {
        label: (
          <Trans i18nKey="manageSubscription.features.localTesting.label">
            <b>Local</b> testing only
          </Trans>
        ),
        icon: <MainFeatureIcon name="home" />,
        tooltip: i18n.t(
          'manageSubscription.features.localTesting.tooltip',
          'Run tests in your own browser without limits.',
        ),
      },
    ],
    otherFeaturesTitle: i18n.t(
      'manageSubscription.plan.free.otherFeaturesTitle',
      "What's included:",
    ),
    features: [
      {
        label: (
          <Trans i18nKey="manageSubscription.features.tests.label">
            <b>Unlimited</b> tests
          </Trans>
        ),
      },
      {
        label: (
          <Trans i18nKey="manageSubscription.features.localRuns.label">
            <b>Unlimited</b> local tests runs
          </Trans>
        ),
        tooltip: i18n.t(
          'manageSubscription.features.localRuns.tooltip',
          'Run tests in your own browser without limits.',
        ),
      },
      {
        label: (
          <Trans i18nKey="manageSubscription.features.suites.label">
            <b>Unlimited</b> suites
          </Trans>
        ),
      },
      {
        label: i18n.t('manageSubscription.features.recordTests.label', 'Record tests'),
        tooltip: i18n.t(
          'manageSubscription.features.recordTests.tooltip',
          'Use the BugBug Chrome Extension to record tests effortlessly—just click in your browser, and it will automatically capture every test step.',
        ),
      },
      {
        label: i18n.t('manageSubscription.features.components.label', 'Components'),
        tooltip: i18n.t(
          'manageSubscription.features.components.tooltip',
          'Components are groups of steps that are shared across multiple tests. Use components to easily edit multiple tests at once.',
        ),
      },
      {
        label: i18n.t('manageSubscription.features.editAndRewind.label', 'Edit & rewind'),
        tooltip: i18n.t(
          'manageSubscription.features.editAndRewind.tooltip',
          'Edit steps in the middle of your test and start the execution from any given position, bypassing the need to wait for earlier steps to complete.',
        ),
      },
      {
        label: i18n.t('manageSubscription.features.conditions.label', 'Smart waiting conditions'),
        tooltip: i18n.t(
          'manageSubscription.features.conditions.tooltip',
          'Before running each test step, BugBug will do a series of checks if your page is ready for a specific action.',
        ),
      },
      {
        label: i18n.t('manageSubscription.features.screenshots.label', 'Screenshots'),
        tooltip: i18n.t(
          'manageSubscription.features.screenshots.tooltip',
          'BugBug takes a full-size screenshot for every action.',
        ),
      },
      {
        label: i18n.t(
          'manageSubscription.plan.free.features.retention.label',
          '7 days of tests history',
        ),
      },
    ],
  },
  [SUBSCRIPTION_TYPE.PRO]: {
    name: i18n.t('manageSubscription.plan.pro.name', 'Pro'),
    description: i18n.t(
      'manageSubscription.plan.pro.description',
      'For growing teams and start-ups who want to start automation testing',
    ),
    mainFeatures: [
      {
        label: (
          <Trans i18nKey="manageSubscription.features.users.label">
            <b>Unlimited</b> users
          </Trans>
        ),
        icon: <MainFeatureIcon name="users" />,
      },
      {
        label: (
          <Trans i18nKey="manageSubscription.features.unlimitedCloudRuns.label">
            <b>Unlimited</b> cloud test runs
          </Trans>
        ),
        icon: <MainFeatureIcon name="uploadCloud" />,
        tooltip: i18n.t(
          'manageSubscription.features.unlimitedCloudRuns.tooltip',
          'Run tests via BugBug cloud virtual machine. You can run tests unlimited number of times.',
        ),
      },
    ],
    otherFeaturesTitle: i18n.t(
      'manageSubscription.plan.pro.otherFeaturesTitle',
      'Everything in FREE, and:',
    ),
    features: [
      {
        label: i18n.t('manageSubscription.features.schedule.label', 'Schedule'),
        tooltip: i18n.t(
          'manageSubscription.features.schedule.tooltip',
          'Monitor your website and stay confident it works as it should.',
        ),
      },
      {
        label: i18n.t('manageSubscription.features.email.label', 'E-mail notifications'),
        tooltip: i18n.t(
          'manageSubscription.features.email.tooltip',
          'Get notifications when tests on schedule failed.',
        ),
      },
      {
        label: i18n.t(
          'manageSubscription.plan.pro.features.slackNotifications.label',
          'Slack notifications',
        ),
        tooltip: i18n.t(
          'manageSubscription.plan.pro.features.slackNotifications.tooltip',
          'Get real-time, detailed updates on test runs, suite executions, or scheduled runs in your Slack messages.',
        ),
      },
      {
        label: i18n.t('manageSubscription.features.integrations.label', 'CI/CD integrations'),
        tooltip: i18n.t(
          'manageSubscription.features.integrations.tooltip',
          'Integrate with GitHub, GitLab, Bitbucket Pipelines, etc.',
        ),
      },
      {
        label: i18n.t('manageSubscription.features.cli.label', 'BugBug CLI'),
        tooltip: i18n.t(
          'manageSubscription.features.cli.tooltip',
          'Integrate with any CI/CD pipeline using BugBug Command Line Interface.',
        ),
      },
      {
        label: i18n.t(
          'manageSubscription.plan.pro.features.customJS.label',
          'Custom JavaScript steps',
        ),
        tooltip: i18n.t(
          'manageSubscription.plan.pro.features.customJS.tooltip',
          'Perform any function during your tests with the power and flexibility of custom code actions.',
        ),
      },
      {
        label: i18n.t('manageSubscription.plan.pro.features.webhooks.label', 'Webhooks'),
        tooltip: i18n.t(
          'manageSubscription.plan.pro.features.webhooks.tooltip',
          "Integrate BugBug into your current workflow, for example by sending notifications to your team's communicator, such as Slack.",
        ),
      },

      {
        label: i18n.t('manageSubscription.features.variables.label', 'Variables & Profiles'),
        tooltip: i18n.t(
          'manageSubscription.features.variables.tooltip',
          'Handle advanced testing scenarios, create your own presets for different variable values, work on multiple environments, generate unique user emails, etc.',
        ),
      },
      {
        label: i18n.t(
          'manageSubscription.plan.pro.features.inbox.label',
          'Inbox for e-mail testing',
        ),
        tooltip: i18n.t(
          'manageSubscription.plan.pro.features.inbox.tooltip',
          'Use the BugBug inbox feature for user registration and login tests. Generate a unique email address, receive the email with the verification link.',
        ),
      },
      {
        label: i18n.t('manageSubscription.plan.pro.features.consoleLogs.label', 'Console logs'),
        tooltip: i18n.t(
          'manageSubscription.plan.pro.features.consoleLogs.tooltip',
          ' Easily identify test issues by collecting browser console logs along with debug logs from the BugBug Chrome extension.',
        ),
      },
      {
        label: i18n.t(
          'manageSubscription.plan.pro.features.pdfReportsForSuiteRuns.label',
          'PDF reports for suite runs',
        ),
        tooltip: i18n.t(
          'manageSubscription.plan.pro.features.pdfReportsForSuiteRuns.tooltip',
          'Download a PDF report for each suite run in your project.',
        ),
      },
      {
        label: i18n.t(
          'manageSubscription.plan.pro.features.retention.label',
          '1 month of tests history',
        ),
      },
      {
        label: i18n.t(
          'manageSubscription.plan.pro.features.optionalParallelRuns.label',
          'Optional parallel runs',
        ),
        tooltip: i18n.t(
          'manageSubscription.plan.pro.features.optionalParallelRuns.tooltip',
          'Run multiple tests simultaneously in the cloud—up to 32x faster.',
        ),
      },
    ],
  },
  [SUBSCRIPTION_TYPE.BUSINESS]: {
    name: i18n.t('manageSubscription.plan.business.name', 'Business'),
    description: i18n.t(
      'manageSubscription.plan.business.description',
      'For scaling companies wanting to speed up their testing process',
    ),
    mainFeatures: [
      {
        label: (
          <Trans i18nKey="manageSubscription.features.users.label">
            <b>Unlimited</b> users
          </Trans>
        ),
        icon: <MainFeatureIcon name="users" />,
      },
      {
        label: (
          <Trans i18nKey="manageSubscription.features.unlimitedCloudRuns.label">
            <b>Unlimited</b> cloud test runs
          </Trans>
        ),
        icon: <MainFeatureIcon name="uploadCloud" />,
        tooltip: i18n.t(
          'manageSubscription.features.unlimitedCloudRuns.tooltip',
          'Run tests via BugBug cloud virtual machine. You can run tests unlimited number of times.',
        ),
      },
    ],
    otherFeaturesTitle: i18n.t(
      'manageSubscription.plan.business.otherFeaturesTitle',
      'Everything in PRO, and:',
    ),
    features: [
      {
        label: i18n.t('manageSubscription.features.api.label', 'REST API access'),
        tooltip: i18n.t(
          'manageSubscription.features.api.tooltip',
          "Run tests and suites in the cloud using BugBug's public API.",
        ),
      },
      {
        label: i18n.t(
          'manageSubscription.features.longerCloudRunTime.label',
          'Longer cloud run time',
        ),
        tooltip: i18n.t(
          'manageSubscription.features.longerCloudRunTime.tooltip',
          'A single cloud test can run for up to 30 minutes.',
        ),
      },
      {
        label: i18n.t(
          'manageSubscription.features.scheduledRunsEveryMinute.label',
          'Scheduled runs every minute',
        ),
        tooltip: i18n.t(
          'manageSubscription.features.scheduledRunsEveryMinute.tooltip',
          'Test run frequency can be scheduled as frequently as every 1 minute.',
        ),
      },
      {
        label: i18n.t(
          'manageSubscription.features.extendedHistory.label',
          '2 months of tests history',
        ),
      },
      {
        label: i18n.t('manageSubscription.features.support.label', 'Priority customer support'),
        tooltip: i18n.t(
          'manageSubscription.features.support.tooltip',
          'Benefit from top-priority support delivered by our test automation specialists.',
        ),
      },
    ],
  },
};
